import React, { forwardRef } from 'react';
import styles from '../../pages/DiagramPage/DiagramEditor.module.css';
import { Shape, Connection, Point, TextElement, FreeLine } from '../../types/diagram.types';
import { ShapeComponent } from './ShapeComponent';
import { TextComponent } from './TextComponent';
import { useLanguage } from '../../context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons';

interface CanvasProps {
  shapes: Shape[];
  connections: Connection[];
  selectedShape: string | null;
  selectedConnection: string | null;
  selectedText: string | null;
  selectedLine: string | null;
  connecting: string | null;
  tempConnection: Point | null;
  pan: Point;
  zoom: number;
  freeLines: FreeLine[];
  drawingLine: FreeLine | null;
  texts: TextElement[];
  tool: 'select' | 'line';
  isEditMode: boolean;
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseMove: (e: React.MouseEvent) => void;
  onMouseUp: (e: React.MouseEvent) => void;
  onWheel: (e: React.WheelEvent) => void;
  onShapeMouseDown: (e: React.MouseEvent, shapeId: string) => void;
  onShapeMouseUp: (e: React.MouseEvent, shapeId: string) => void;
  onConnectionClick: (connId: string) => void;
  onShapeDoubleClick: (shapeId: string) => void;
  onResizeStart: (
    e: React.MouseEvent,
    shapeId: string,
    direction: 'se' | 'sw' | 'ne' | 'nw'
  ) => void;
  onTextMouseDown: (e: React.MouseEvent, textId: string) => void;
  onTextDoubleClick: (textId: string) => void;
}

export const Canvas = forwardRef<HTMLDivElement, CanvasProps>(({
  shapes,
  connections,
  selectedShape,
  selectedConnection,
  selectedText,
  selectedLine,
  connecting,
  tempConnection,
  pan,
  zoom,
  onMouseDown,
  onMouseMove,
  onMouseUp,
  onWheel,
  onShapeMouseDown,
  onShapeMouseUp,
  onConnectionClick,
  onShapeDoubleClick,
  onResizeStart,
  freeLines,
  drawingLine,
  texts,
  tool,
  isEditMode,
  onTextMouseDown,
  onTextDoubleClick,
}, ref) => {
  const { t } = useLanguage();

  const renderLine = (line: FreeLine, isSelected: boolean = false) => (
    <g key={line.id}>
      <path
        className={`${styles.freeLine} ${isSelected ? styles.freeLineSelected : ''}`}
        d={`M ${line.points.map(p => `${p.x} ${p.y}`).join(' L ')}`}
        fill="none"
        stroke={isSelected ? '#3b82f6' : line.color}
        strokeWidth={isSelected ? line.width + 2 : line.width}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      
      <path
        className={styles.freeLineClickArea}
        d={`M ${line.points.map(p => `${p.x} ${p.y}`).join(' L ')}`}
        fill="none"
        stroke="transparent"
        strokeWidth={Math.max(line.width + 10, 15)}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {isSelected && isEditMode && line.points.map((point, idx) => (
        <circle
          key={`point-${idx}`}
          className={styles.freeLinePoint}
          cx={point.x}
          cy={point.y}
          r={4}
          fill="#3b82f6"
          stroke="white"
          strokeWidth={2}
        />
      ))}
    </g>
  );

  return (
    <div
      ref={ref}
      className={`${styles.canvas} ${tool === 'line' && isEditMode ? styles.canvasCrosshair : ''} ${!isEditMode ? styles.canvasViewMode : ''}`}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onWheel={onWheel}
      onContextMenu={(e) => e.preventDefault()}
    >
      {tool === 'line' && isEditMode && (
        <div className={styles.drawingModeIndicator}>
          <FontAwesomeIcon icon={faPen} style={{ marginRight: 6 }} />
          {t('drawingModeHint')}
        </div>
      )}

      <div
        className={styles.canvasContent}
        style={{
          transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
        }}
      >
        <svg
          className={styles.connectionLayer}
          width="100%"
          height="100%"
        >
          {freeLines.map(line => renderLine(line, selectedLine === line.id))}
          
          {drawingLine && isEditMode && (
            <path
              className={styles.drawingLine}
              d={`M ${drawingLine.points.map(p => `${p.x} ${p.y}`).join(' L ')}`}
              fill="none"
              stroke={drawingLine.color}
              strokeWidth={drawingLine.width}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {connections.map(conn => (
            <line
              key={conn.id}
              className={styles.connection}
              x1={conn.fromPoint.x}
              y1={conn.fromPoint.y}
              x2={conn.toPoint.x}
              y2={conn.toPoint.y}
              stroke={conn.color}
              strokeWidth={conn.width}
              strokeDasharray={conn.type === 'dashed' ? '6,4' : undefined}
              markerEnd={conn.type === 'arrow' ? 'url(#arrowhead)' : undefined}
              onClick={(e) => {
                e.stopPropagation();
                onConnectionClick(conn.id);
              }}
            />
          ))}

          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="10"
              refX="9"
              refY="3"
              orient="auto"
            >
              <polygon points="0 0, 10 3, 0 6" fill="#1e3a8a" />
            </marker>
          </defs>
        </svg>

        {shapes.map(shape => (
          <ShapeComponent
            key={shape.id}
            shape={shape}
            isSelected={selectedShape === shape.id}
            isEditMode={isEditMode}
            onMouseDown={(e) => onShapeMouseDown(e, shape.id)}
            onMouseUp={(e) => onShapeMouseUp(e, shape.id)}
            onDoubleClick={() => onShapeDoubleClick(shape.id)}
            onResizeStart={(e, dir) =>
              onResizeStart(e, shape.id, dir)
            }
          />
        ))}

        {texts.map(text => (
          <TextComponent
            key={text.id}
            text={text}
            isSelected={selectedText === text.id}
            isEditMode={isEditMode}
            onMouseDown={(e) => onTextMouseDown(e, text.id)}
            onDoubleClick={() => onTextDoubleClick(text.id)}
          />
        ))}
      </div>
    </div>
  );
});

Canvas.displayName = 'Canvas';